/**
 * DataGapsPulseCard — surfaces "fill-this-missing-field" tasks assigned
 * to the operator viewing their own Pulse (or to the team member whose
 * profile is being inspected by a Super Admin / Supervisor).
 *
 * Layout: a single-line chip (Open · Done) that expands into a sheet
 * listing the open tasks. Each task row launches the same inline-fill
 * sheet used on the dashboard (via deep-link to /pollen-dots → tile).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Loader2, ListChecks, Check, RotateCcw, Mail, Phone, Map, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const FIELD_META = {
  email:    { icon: Mail,   label: 'Email' },
  phone:    { icon: Phone,  label: 'Contact #' },
  pin:      { icon: Map,    label: 'Map pin' },
  location: { icon: MapPin, label: 'Location' },
};

const fetcher = async (path, opts = {}) => {
  const t = localStorage.getItem('session_token');
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}), ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

export default function DataGapsPulseCard({ counts, assigneeEmail, onChange }) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [autoTab, setAutoTab] = useState(null);

  // Deep-link from NotificationBell — supervisor lands here after an
  // auto-close ping. ?datagaps=done auto-opens the sheet on the Done tab.
  useEffect(() => {
    const usp = new URLSearchParams(window.location.search);
    const dg = usp.get('datagaps');
    if (dg === 'done' || dg === 'open') {
      setAutoTab(dg === 'done' ? 'Done' : 'Open');
      setSheetOpen(true);
    }
  }, []);

  const open = counts?.open ?? 0;
  const done = counts?.done ?? 0;
  if (!assigneeEmail) return null;
  return (
    <Card data-testid="pulse-data-gaps-card">
      <CardContent className="p-3 sm:p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <ListChecks className="w-4 h-4 text-amber-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Data-gap tasks</p>
            <p className="text-[11px] text-muted-foreground">Fill missing vendor fields directly from your queue.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`text-[11px] ${open > 0 ? 'border-amber-400 bg-amber-50 text-amber-900' : ''}`} data-testid="gaps-open-count">
            {open} open
          </Badge>
          <Badge variant="outline" className="text-[11px]" data-testid="gaps-done-count">{done} done</Badge>
          <Button size="sm" variant="outline" onClick={() => setSheetOpen(true)} data-testid="open-pulse-gaps">
            View
          </Button>
        </div>
        <PulseGapTasksSheet
          open={sheetOpen}
          onOpenChange={(v) => { setSheetOpen(v); if (!v && onChange) onChange(); }}
          assigneeEmail={assigneeEmail}
          initialTab={autoTab || 'Open'}
        />
      </CardContent>
    </Card>
  );
}

function PulseGapTasksSheet({ open, onOpenChange, assigneeEmail, initialTab = 'Open' }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(initialTab); // Open | Done

  // Sync to the latest initialTab when the parent re-opens with a new value
  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);

  const load = useCallback(() => {
    if (!assigneeEmail) return;
    setLoading(true);
    fetcher(`/pollen/data-quality/tasks?assignee_email=${encodeURIComponent(assigneeEmail)}&status=${tab}`)
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [assigneeEmail, tab]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const flipStatus = async (id, next) => {
    try {
      await fetcher(`/pollen/data-quality/tasks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: next }),
      });
      toast.success(`Marked ${next}`);
      load();
    } catch { toast.error('Could not update'); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="z-[80] max-h-[88vh] overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+5rem)] lg:pb-6 rounded-t-2xl" data-testid="pulse-gap-sheet">
        <SheetHeader>
          <SheetTitle className="text-base">Data-gap tasks for you</SheetTitle>
          <SheetDescription className="text-[11px]">
            Tap a task to open the inline editor on the dashboard. Mark Done when the gap is filled.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" variant={tab === 'Open' ? 'default' : 'outline'} onClick={() => setTab('Open')} data-testid="pulse-tab-open">Open</Button>
          <Button size="sm" variant={tab === 'Done' ? 'default' : 'outline'} onClick={() => setTab('Done')} data-testid="pulse-tab-done">Done</Button>
        </div>

        <div className="mt-3 space-y-2">
          {loading ? (
            <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>
          ) : rows.length === 0 ? (
            <p className="text-xs text-muted-foreground">No {tab.toLowerCase()} data-gap tasks.</p>
          ) : rows.map(t => {
            const meta = FIELD_META[t.field] || { icon: ListChecks, label: t.field };
            const Icon = meta.icon;
            return (
              <div key={t.id} className="border rounded-lg p-2 flex items-start gap-2" data-testid={`pulse-gap-task-${t.id}`}>
                <Icon className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.title}</div>
                  <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-2">
                    <span>{(t.vendor_ids || []).length} vendor(s)</span>
                    <span>· assigned by {t.assigned_by_name || t.assigned_by_email}</span>
                    <span>· {new Date(t.created_at).toLocaleDateString()}</span>
                  </div>
                  {t.note && <p className="text-[11px] text-muted-foreground mt-0.5">"{t.note}"</p>}
                </div>
                <div className="flex flex-col gap-1">
                  {tab === 'Open' ? (
                    <>
                      <a
                        href={`/pollen-dots-dashboard?gap=${t.field}&task=${encodeURIComponent(t.id)}`}
                        className="h-8 px-2.5 inline-flex items-center justify-center rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 text-xs font-medium"
                        title="Open inline editor pre-filtered to this task"
                        data-testid={`pulse-gap-fill-${t.id}`}
                      >
                        Fill
                      </a>
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-700" onClick={() => flipStatus(t.id, 'Done')} data-testid={`pulse-gap-done-${t.id}`}>
                        <Check className="w-3.5 h-3.5 mr-1" />Done
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => flipStatus(t.id, 'Open')} data-testid={`pulse-gap-reopen-${t.id}`}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />Reopen
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
